/**
 * Last-good cache for the Alternative Data page.
 *
 * The page fans out 32 live external-API fetches. When the whole cycle fails
 * (e.g. the server IP is rate-limited across the board), every panel array
 * comes back empty and the page goes blank — even though we had perfectly good
 * data a minute ago. These helpers persist the last *non-empty* payload to
 * localStorage so a transient outage shows stale-but-real data (clearly
 * marked) instead of nothing, and a page reload rehydrates instantly while the
 * fresh fetch runs.
 *
 * Storage round-trip is delegated to the generic snapshotCache helper; this
 * module keeps the alt-data-specific emptiness check and a stable public API.
 */

import {
  saveSnapshot, loadSnapshot, clearSnapshot, type Snapshot,
} from './snapshotCache'

const KEY = 'fg-altdata-snapshot-v1'

export type AltSnapshot = Snapshot<Record<string, unknown>>

/**
 * True when a derived alt-data payload carries no usable content — every array
 * empty, every scalar null/blank. Generic over the field set (sums array
 * lengths + counts non-null scalars) so adding a new panel doesn't silently
 * break the emptiness check. Meta keys are never passed in here; callers hand
 * us only the panel-data slice.
 */
export function isAltDataEmpty(data: Record<string, unknown>): boolean {
  for (const v of Object.values(data)) {
    if (Array.isArray(v)) {
      if (v.length > 0) return false
    } else if (v === null || v === undefined) {
      continue
    } else if (typeof v === 'number') {
      if (Number.isFinite(v)) return false
    } else if (typeof v === 'string') {
      if (v.trim().length > 0) return false
    } else if (typeof v === 'object') {
      // A non-null object (e.g. recessionSignals, putCallRatio) counts as data.
      return false
    } else if (typeof v === 'boolean') {
      if (v) return false
    }
  }
  return true
}

/** Persist the last non-empty payload. No-op on any storage failure. */
export function saveAltSnapshot(data: Record<string, unknown>, now: number): void {
  saveSnapshot(KEY, data, now)
}

/** Load the last snapshot, or null if absent/corrupt/unavailable. */
export function loadAltSnapshot(): AltSnapshot | null {
  return loadSnapshot<Record<string, unknown>>(KEY)
}

/** Drop the cached snapshot (e.g. a manual "clear cached data" action). */
export function clearAltSnapshot(): void {
  clearSnapshot(KEY)
}
